import { eventTypeChipForSlug } from '@/modules/public/search/services/event-type-chips';
import getSearchLabelledFiltersList from '@/modules/public/search/services/labelled-filters-list';

const getters = {
  visibleRooms: state => state.rooms.slice(0, state.visibleRoomsNumber),
  nonVisibleRooms: state => state.rooms.slice(state.visibleRoomsNumber),
  roomsNumber: state => state.rooms.length,
  roomsIds: state => state.rooms.map(room => room.room_id),
  pageCount: state => Math.ceil(state.totalDisplayedRooms / state.perPage),
  getRoom: state => roomId => state.rooms.filter(room => room.room_id === roomId)[0],
  resultListVenueIds: state => state.rooms.map(room => room.venue_id),
  roomsSlice: state => state.rooms.slice(0, state.displayedRooms),
  roomsMap: state => {
    const map = {};
    state.rooms.forEach(room => {
      map[room.room_id] = room;
    });
    return map;
  },
  currentPriceMethodBoundaries: state => {
    if (state.filters.priceMethod.value === 'per_person') {
      return [...state.priceBoundaries.per_person];
    }

    return [...state.priceBoundaries.default];
  },
  priceRangeWithDefaults: (state, _getters) => {
    const [boundaryMin, boundaryMax] = _getters.currentPriceMethodBoundaries;
    const { from, to } = state.filters.priceRange.value || [];
    return [from || boundaryMin, to || boundaryMax];
  },
  isMinPriceFilterDefault: (state, _getters) => {
    const { from } = state.filters.priceRange.value;
    return !from || from === _getters.currentPriceMethodBoundaries[0];
  },
  isMaxPriceFilterDefault: (state, _getters) => {
    const { to } = state.filters.priceRange.value;
    return !to || to === _getters.currentPriceMethodBoundaries[1];
  },
  isPriceFilterDefault: (state, _getters) => {
    return _getters.isMinPriceFilterDefault && _getters.isMaxPriceFilterDefault;
  },
  appliedAccessibilityFilters: state => {
    return Object.keys(state.filters.accessibilityFeatures.value)
      .filter(key => state.filters.accessibilityFeatures.value[key]);
  },
  appliedFilters: (state, _getters) => {
    const filters = [
      'people',
      'layout',
      'date',
      'timeFrom',
      'timeTo',
      'location',
      'priceRange',
      'priceMethod',
      'catering',
      'roomFeatures',
      'allowedEvents',
      'venueFeatures',
      'superVenuesOnly',
      'flexibleCancellation',
      'venueParts',
      'venueTypes',
      'noAgeRestriction',
      'accessibilityFilters',
    ];

    return filters.filter((filter) => {
      if (filter === 'priceRange') {
        return !_getters.isPriceFilterDefault;
      }

      if (filter === 'venueParts') {
        return state.filters.venueParts.value.length > 0;
      }

      if (filter === 'accessibilityFilters') {
        return _getters.appliedAccessibilityFilters.length > 0;
      }

      if (Array.isArray(state.filters[filter].value)) {
        return state.filters[filter].value.length > 0;
      }

      return !!state.filters[filter].value;
    });
  },
  isNoFiltersApplied: (state, _getters) => {
    if (_getters.appliedFilters.includes('location') && _getters.appliedFilters.length === 1) {
      return true;
    }
    return _getters.appliedFilters.length === 0;
  },
  selectedDateMoment(state) {
    if (!state.filters.date.value) {
      return null;
    }

    return window.dayjs(state.filters.date.value, 'DD/MM/YYYY');
  },
  selectedDateMonth(state, _getters) {
    if (!state.filters.date.value) {
      return null;
    }

    return _getters.selectedDateMoment.month() + 1;
  },
  selectedDateDate(state, _getters) {
    if (!state.filters.date.value) {
      return null;
    }

    return _getters.selectedDateMoment.date();
  },
  selectedDateMonthLong(state, _getters) {
    if (!state.filters.date.value) {
      return null;
    }

    return _getters.selectedDateMoment.format('MMMM');
  },
  isChristmasSeason(state, _getters) {
    if (!state.filters.date.value) {
      return null;
    }

    const month = _getters.selectedDateMonth;
    const date = _getters.selectedDateDate;

    return (month === 11 && date >= 22) || (month === 12 && date <= 24);
  },
  isChristmasParty(state) {
    return state.christmasEventTypes.indexOf(state.eventTypeSlug) !== -1;
  },
  christmasLabel(state, _getters) {
    if (_getters.isChristmasDinner) {
      return 'Christmas Dinner';
    }

    if (state.l10n.ISOCountryCode === 'US' || state.l10n.ISOCountryCode === 'CA') {
      return 'Holiday Party';
    }

    return 'Christmas Party';
  },
  isChristmasDinner(state) {
    return state.christmasDiningEventTypes.indexOf(state.eventTypeSlug) !== -1;
  },
  hasPricingMethodFilter(state) {
    return !state.roomTagsWithoutPricingMethodFilter.includes(state.eventTypeSlug);
  },
  ga4ParamsBase(state) {
    const date = state.filters.date.value
      ? window.dayjs(state.filters.date.value, 'DD/MM/YYYY')
      : null;

    return {
      location_name: state.filters.location.value,
      country: state.l10n.ISOCountryCode,
      event_date: date?.format('YYYY-MM-DD') || null,
      event_tag_slug: state.eventTypeSlug,
      number_of_people: state.filters.people.value || null,
      results: state.totalRooms,
      city: state.trackingCity,
    };
  },
  mapCenter(state) {
    return {
      lat: (state.boundaries.latitude_to + state.boundaries.latitude_from) / 2,
      long: (state.boundaries.longitude_to + state.boundaries.longitude_from) / 2,
    };
  },
  ga4Params(state, _getters) {
    const center = _getters.mapCenter;

    return {
      ..._getters.ga4ParamsBase,
      map_coordinates: `${center.lat},${center.long}`,
      map_zoom_level: state.mapZoom,
      price_min: state.filters.priceRange.value.from,
      price_max: state.filters.priceRange.value.to,
    };
  },
  debugFilterModeRoomsDisplayed(state) {
    if (!state.debugFilterModeRooms) {
      return 0;
    }

    return state.rooms.slice(0, state.perPage)
      .filter(room => state.debugFilterModeRooms[room.room_id])
      .length;
  },
  debugFilterModeRoomsDisplayedAll(state) {
    return Math.min(state.rooms.length, state.debugFilterModeMaxRooms);
  },
  selectedOptionsCount(state, _getters) {
    const { filters } = state;
    let result = 0;
    result += filters.catering.value.length;
    result += filters.roomFeatures.value.length;
    result += filters.allowedEvents.value.length;
    result += filters.venueFeatures.value.length;
    result += filters.flexibleCancellation.value ? 1 : 0;
    result += filters.noAgeRestriction.value ? 1 : 0;
    result += filters.superVenuesOnly.value ? 1 : 0;
    result += filters.venueParts.value.length;
    result += filters.venueTypes.value.length;
    result += _getters.appliedAccessibilityFilters.length;
    result += !_getters.isPriceFilterDefault ? 1 : 0;
    result += filters.priceMethod.value ? 1 : 0;
    result += filters.people.value ? 1 : 0;
    result += filters.layout.value ? 1 : 0;
    result += (filters.date.value || filters.timeFrom.value || filters.timeTo.value) ? 1 : 0;

    return result;
  },
  experimentChipsFiltersVisible(state) {
    return state.quickFilters.length;
  },
  // The event-type chip that switched the search to the current event type – shown selected while the search stays
  activeEventTypeChip(state) {
    if (!state.fromRoomTagSlug || state.fromRoomTagSlug === state.eventTypeSlug) {
      return null;
    }

    return eventTypeChipForSlug(state.eventTypeSlug, state.l10n.ISOCountryCode);
  },
  getSearchLabelledFiltersList,
};

export default getters;
